import React, { useEffect, useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiX, FiChevronRight, FiChevronLeft, FiChevronDown, FiUsers } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { employeesService, expStatus } from "services";
import "./MobileExpenseFiltersSheet.scss";

// ─── Hook: altura real del viewport (sube el sheet sobre el teclado) ─────────

function useViewportHeight(enabled) {
	const [height, setHeight] = useState(null);

	useEffect(() => {
		if (!enabled) {
			setHeight(null);
			return undefined;
		}
		const vv = window.visualViewport;
		if (!vv) return undefined;

		const handler = () => setHeight(vv.height);
		vv.addEventListener("resize", handler);
		handler();

		return () => vv.removeEventListener("resize", handler);
	}, [enabled]);

	return height;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (str) => {
	if (!str) return null;
	const [y, m, d] = str.split("-");
	return `${d}/${m}/${y}`;
};

const strToDate = (str) => {
	if (!str) return null;
	return new Date(`${str}T00:00:00`);
};

const dateToStr = (d) => {
	if (!d) return null;
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
};

// ─── Sub-view: lista genérica (proveedor / categoría) ────────────────────────

const FilterListSubView = ({
	title,
	items,
	value,
	onChange,
	onBack,
	allLabel,
}) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");

	const filtered = useMemo(
		() =>
			(items ?? []).filter((item) =>
				item.name.toLowerCase().includes(search.toLowerCase())
			),
		[items, search]
	);

	return (
		<div className="mefs-subview">
			<div className="mefs-subview__header">
				<button
					type="button"
					className="mefs-subview__back"
					onClick={onBack}
					aria-label={t("common.back")}
				>
					<FiChevronLeft size={22} />
				</button>
				<span className="mefs-subview__title">{title}</span>
			</div>

			<div className="mefs-subview__search-wrap">
				<input
					className="mefs-subview__search"
					placeholder={t("expenses.mobile.filters.search", { title: title.toLowerCase() })}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>

			<div className="mefs-subview__list">
				<button
					type="button"
					className={`mefs-subview__item${!value ? " mefs-subview__item--selected" : ""}`}
					onClick={() => {
						onChange("");
						onBack();
					}}
				>
					<span>{allLabel}</span>
					{!value && <span className="mefs-subview__check" aria-hidden>✓</span>}
				</button>

				{filtered.map((item) => {
					const isSelected = String(value) === String(item.id);
					return (
						<button
							key={item.id}
							type="button"
							className={`mefs-subview__item${isSelected ? " mefs-subview__item--selected" : ""}`}
							onClick={() => {
								onChange(String(item.id));
								onBack();
							}}
						>
							<span>{item.name}</span>
							{isSelected && (
								<span className="mefs-subview__check" aria-hidden>✓</span>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
};


// ─── Sub-view: rango de fechas con calendario ─────────────────────────────────

const FilterDatesSubView = ({
	focusField, // "start" | "end" — indica qué campo abrió la vista
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	onBack,
}) => {
	const { t } = useTranslation();
	const start = strToDate(startDate);
	const end = strToDate(endDate);

	const handleClear = () => {
		onStartDateChange(null);
		onEndDateChange(null);
	};

	const hasRange = startDate || endDate;

	return (
		<div className="mefs-subview mefs-subview--dates">
			<div className="mefs-subview__header">
				<button
					type="button"
					className="mefs-subview__back"
					onClick={onBack}
					aria-label={t("common.back")}
				>
					<FiChevronLeft size={22} />
				</button>
				<span className="mefs-subview__title">{t("expenses.mobile.filters.dateRange")}</span>
				{hasRange && (
					<button
						type="button"
						className="mefs-subview__clear-btn"
						onClick={handleClear}
					>
						{t("expenses.mobile.filters.clear")}
					</button>
				)}
			</div>

			<div className="mefs-subview__date-summary">
				<span className={`mefs-subview__date-chip${focusField === "start" ? " mefs-subview__date-chip--focus" : ""}`}>
					<span className="mefs-subview__date-chip-label">{t("expenses.mobile.filters.from")}</span>
					<span className="mefs-subview__date-chip-value">
						{startDate ? fmtDate(startDate) : "—"}
					</span>
				</span>
				<span className="mefs-subview__date-arrow" aria-hidden>→</span>
				<span className={`mefs-subview__date-chip${focusField === "end" ? " mefs-subview__date-chip--focus" : ""}`}>
					<span className="mefs-subview__date-chip-label">{t("expenses.mobile.filters.to")}</span>
					<span className="mefs-subview__date-chip-value">
						{endDate ? fmtDate(endDate) : "—"}
					</span>
				</span>
			</div>

			<div className="mefs-subview__calendar-wrap">
				{focusField === "start" ? (
					<DatePicker
						selectsStart
						selected={start}
						startDate={start}
						endDate={end}
						openToDate={start ?? new Date()}
						onChange={(d) => { onStartDateChange(d ? dateToStr(d) : null); onBack(); }}
						inline
						calendarClassName="mefs-calendar"
						maxDate={new Date()}
					/>
				) : (
					<DatePicker
						selectsEnd
						selected={end}
						startDate={start}
						endDate={end}
						minDate={start || undefined}
						openToDate={end ?? new Date()}
						onChange={(d) => { onEndDateChange(d ? dateToStr(d) : null); onBack(); }}
						inline
						calendarClassName="mefs-calendar"
						maxDate={new Date()}
					/>
				)}
			</div>

			<div className="mefs-subview__footer">
				<button
					type="button"
					className="btn btn-primary btn-block"
					onClick={onBack}
				>
					{t("expenses.mobile.filters.confirm")}
				</button>
			</div>
		</div>
	);
};

// ─── Sub-view: selector de empleado ──────────────────────────────────────────

const FilterEmployeeSubView = ({ value, onChange, onBack }) => {
	const { t } = useTranslation();
	const [employees, setEmployees] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		employeesService
			.getSelectable({ page_size: 500, enabled: true })
			.then((res) => {
				if (!cancelled) setEmployees(res.data ?? []);
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const handleSelect = (id, emp) => {
		onChange(id || null, emp);
		onBack();
	};

	return (
		<div className="mefs-subview">
			<div className="mefs-subview__header mefs-subview__header--centered">
				<span className="mefs-subview__title">{t("common.employee")}</span>
				<button
					type="button"
					className="mefs-subview__close"
					onClick={onBack}
					aria-label={t("common.cancel")}
				>
					<FiX size={20} />
				</button>
			</div>

			<div className="mefs-subview__list">
				<button
					type="button"
					className={`mefs-employee-item${!value ? " mefs-employee-item--selected" : ""}`}
					onClick={() => handleSelect("", null)}
				>
					<div className="mefs-employee-item__avatar mefs-employee-item__avatar--all">
						<FiUsers size={18} />
					</div>
					<span className="mefs-employee-item__name">{t("expenses.allEmployees")}</span>
				</button>

				{loading ? (
					<div className="mefs-employee-item mefs-employee-item--loading">
						{t("expenses.mobile.filters.loadingEmployees")}
					</div>
				) : employees.map((emp) => {
					const id = String(emp.id);
					const isSelected = String(value) === id;
					const initials = `${emp.name?.charAt(0) ?? ""}${emp.surname?.charAt(0) ?? ""}`.toUpperCase();
					return (
						<button
							key={emp.id}
							type="button"
							className={`mefs-employee-item${isSelected ? " mefs-employee-item--selected" : ""}`}
							onClick={() => handleSelect(id, emp)}
						>
							<div className="mefs-employee-item__avatar">
								{initials || "?"}
							</div>
							<div className="mefs-employee-item__info">
								<span className="mefs-employee-item__name">
									{`${emp.name ?? ""} ${emp.surname ?? ""}`.trim()}
								</span>
								{emp.code && (
									<span className="mefs-employee-item__code">
										{emp.code}
									</span>
								)}
							</div>
						</button>
					);
				})}
			</div>

			<div className="mefs-subview__footer">
				<button
					type="button"
					className="btn btn-outline-secondary btn-block"
					onClick={onBack}
				>
					{t("common.cancel")}
				</button>
			</div>
		</div>
	);
};

// ─── Componente principal ─────────────────────────────────────────────────────

const STATUS_COLORS = {
	[expStatus.pending]: "pendiente",
	[expStatus.approved]: "aprobado",
	[expStatus.denied]: "denegado",
	[expStatus.canceled]: "cancelado",
};

const EMPTY_DRAFT = {
	employeeId: null,
	employeeObj: null,
	providerId: "",
	expenseTypeId: "",
	status: "",
	startDate: null,
	endDate: null,
};

const MobileExpenseFiltersSheet = ({
	open,
	onClose,
	approveMode,
	canSupervise,
	filterEmployeeId,
	filterEmployeeName,
	onEmployeeChange,
	filterProviderId,
	filterExpenseTypeId,
	filterStatus,
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	onFilterProviderChange,
	onFilterExpenseTypeChange,
	onFilterStatusChange,
	providers,
	expense_types,
	expense_status,
}) => {
	const { t } = useTranslation();
	const [activeView, setActiveView] = useState("main");
	const [activeDateField, setActiveDateField] = useState("start");
	const [draft, setDraft] = useState(EMPTY_DRAFT);
	const viewportHeight = useViewportHeight(open);

	// Al abrir, inicializar draft con los filtros actualmente aplicados
	useEffect(() => {
		if (open) {
			setActiveView("main");
			setDraft({
				employeeId: filterEmployeeId ?? null,
				employeeObj: filterEmployeeName ? { name: filterEmployeeName } : null,
				providerId: filterProviderId ?? "",
				expenseTypeId: filterExpenseTypeId ?? "",
				status: filterStatus ?? "",
				startDate: startDate ?? null,
				endDate: endDate ?? null,
			});
		}
	}, [open]); // eslint-disable-line

	useEffect(() => {
		if (!open) return undefined;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [open]);

	if (!open) return null;

	// ── Helpers de draft ──────────────────────────────────────────────────────

	const setDraftEmployee = (id, emp) =>
		setDraft((d) => ({ ...d, employeeId: id ?? null, employeeObj: emp ?? null }));

	const setDraftProvider = (val) =>
		setDraft((d) => ({ ...d, providerId: val }));

	const setDraftCategory = (val) =>
		setDraft((d) => ({ ...d, expenseTypeId: val }));

	const setDraftStatus = (name) =>
		setDraft((d) => ({ ...d, status: d.status === name ? "" : name }));

	const setDraftStartDate = (val) =>
		setDraft((d) => ({ ...d, startDate: val }));

	const setDraftEndDate = (val) =>
		setDraft((d) => ({ ...d, endDate: val }));

	const handleClearDraft = () => setDraft(EMPTY_DRAFT);

	// ── Aplicar filtros al padre y cerrar ─────────────────────────────────────

	const handleApply = () => {
		if (canSupervise) {
			onEmployeeChange(draft.employeeId, draft.employeeObj);
		}
		onFilterProviderChange({ target: { value: draft.providerId } });
		onFilterExpenseTypeChange({ target: { value: draft.expenseTypeId } });
		if (!approveMode) {
			onFilterStatusChange({ target: { value: draft.status } });
		}
		onStartDateChange(draft.startDate);
		onEndDateChange(draft.endDate);
		onClose();
	};

	// ── Derivados para la vista ───────────────────────────────────────────────

	const activeFilterCount = [
		canSupervise && draft.employeeId,
		draft.providerId,
		draft.expenseTypeId,
		!approveMode && draft.status,
		draft.startDate || draft.endDate,
	].filter(Boolean).length;

	const providerName =
		providers?.find((p) => String(p.id) === String(draft.providerId))?.name;
	const categoryName =
		expense_types?.find((t) => String(t.id) === String(draft.expenseTypeId))?.name;

	const employeeLabel = draft.employeeObj
		? (typeof draft.employeeObj.name === "string"
			? `${draft.employeeObj.name} ${draft.employeeObj.surname ?? ""}`.trim()
			: filterEmployeeName)
		: null;

	const statusList = expense_status ?? [
		{ id: 1, name: expStatus.pending },
		{ id: 2, name: expStatus.approved },
		{ id: 3, name: expStatus.denied },
		{ id: 4, name: expStatus.canceled },
	];

	return (
		<div
			className="mobile-expense-filters-sheet-backdrop"
			style={viewportHeight != null ? { height: viewportHeight } : undefined}
			onClick={activeView === "main" ? onClose : undefined}
			role="presentation"
		>
			<div
				className="mobile-expense-filters-sheet"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-label={t("expenses.mobile.filters.title")}
			>
				{/* ── Vista principal ─────────────────────────── */}
				{activeView === "main" && (
					<>
						<div className="mobile-expense-filters-sheet__header">
							<div className="mobile-expense-filters-sheet__header-left">
								<h3 className="mobile-expense-filters-sheet__title">{t("expenses.mobile.filters.title")}</h3>
								{activeFilterCount > 0 && (
									<span className="mobile-expense-filters-sheet__badge">
										{activeFilterCount}
									</span>
								)}
							</div>
							<div className="mobile-expense-filters-sheet__header-right">
								{activeFilterCount > 0 && (
									<button
										type="button"
										className="mobile-expense-filters-sheet__clear"
										onClick={handleClearDraft}
									>
										{t("expenses.mobile.filters.clearAll")}
									</button>
								)}
								<button
									type="button"
									className="mobile-expense-filters-sheet__close"
									onClick={onClose}
									aria-label={t("common.close")}
								>
									<FiX size={22} />
								</button>
							</div>
						</div>

						<div className="mobile-expense-filters-sheet__body">
							{/* Empleado */}
							{canSupervise && (
								<button
									type="button"
									className="mefs-row"
									onClick={() => setActiveView("employee")}
								>
									<span className="mefs-row__label">{t("common.employee")}</span>
									<span
										className={`mefs-row__value${draft.employeeId ? " mefs-row__value--active" : ""}`}
									>
										{employeeLabel || (draft.employeeId ? t("expenses.mobile.filters.selected") : t("expenses.mobile.filters.all"))}
									</span>
									<FiChevronRight size={18} className="mefs-row__arrow" />
								</button>
							)}

							{/* Proveedor */}
							<button
								type="button"
								className="mefs-row"
								onClick={() => setActiveView("provider")}
							>
								<span className="mefs-row__label">{t("expenses.mobile.filters.provider")}</span>
								<span
									className={`mefs-row__value${draft.providerId ? " mefs-row__value--active" : ""}`}
								>
									{providerName || t("expenses.mobile.filters.all")}
								</span>
								<FiChevronRight size={18} className="mefs-row__arrow" />
							</button>

							{/* Categoría */}
							<button
								type="button"
								className="mefs-row"
								onClick={() => setActiveView("category")}
							>
								<span className="mefs-row__label">{t("expenses.mobile.filters.category")}</span>
								<span
									className={`mefs-row__value${draft.expenseTypeId ? " mefs-row__value--active" : ""}`}
								>
									{categoryName || t("expenses.mobile.filters.allFem")}
								</span>
								<FiChevronRight size={18} className="mefs-row__arrow" />
							</button>

							{/* Estado — pills inline */}
							{!approveMode && (
								<div className="mefs-section">
									<span className="mefs-section__label">{t("common.status")}</span>
									<div className="mefs-pills">
										{statusList.map((item) => {
											const mod = STATUS_COLORS[item.name] || "unknown";
											const isActive = draft.status === item.name;
											return (
												<button
													key={item.id}
													type="button"
													className={`mefs-pill mefs-pill--${mod}${isActive ? " mefs-pill--active" : ""}`}
													onClick={() => setDraftStatus(item.name)}
												>
													{t(`expenses.statusLabels.${item.name}`, item.name)}
												</button>
											);
										})}
									</div>
								</div>
							)}

							{/* Fechas */}
							<div className="mefs-section">
								<span className="mefs-section__label">{t("expenses.mobile.filters.dates")}</span>
								<div className="mefs-date-pickers">
									<button
										type="button"
										className={`mefs-date-picker${draft.startDate ? " mefs-date-picker--active" : ""}`}
										onClick={() => { setActiveDateField("start"); setActiveView("dates"); }}
									>
										<div className="mefs-date-picker__content">
											<span className="mefs-date-picker__label">{t("expenses.mobile.filters.from")}</span>
											<span className="mefs-date-picker__value">
												{draft.startDate ? fmtDate(draft.startDate) : t("expenses.mobile.filters.select")}
											</span>
										</div>
										<FiChevronDown size={16} className="mefs-date-picker__chevron" />
									</button>
									<button
										type="button"
										className={`mefs-date-picker${draft.endDate ? " mefs-date-picker--active" : ""}`}
										onClick={() => { setActiveDateField("end"); setActiveView("dates"); }}
									>
										<div className="mefs-date-picker__content">
											<span className="mefs-date-picker__label">{t("expenses.mobile.filters.to")}</span>
											<span className="mefs-date-picker__value">
												{draft.endDate ? fmtDate(draft.endDate) : t("expenses.mobile.filters.select")}
											</span>
										</div>
										<FiChevronDown size={16} className="mefs-date-picker__chevron" />
									</button>
								</div>
							</div>
						</div>

						<div className="mobile-expense-filters-sheet__footer">
							<button
								type="button"
								className="btn btn-primary btn-block"
								onClick={handleApply}
							>
								{t("expenses.mobile.filters.viewResults")}
							</button>
						</div>
					</>
				)}

				{/* ── Sub-vista: empleado ──────────────────────── */}
				{activeView === "employee" && (
					<FilterEmployeeSubView
						value={draft.employeeId}
						onChange={setDraftEmployee}
						onBack={() => setActiveView("main")}
					/>
				)}

				{/* ── Sub-vista: proveedor ─────────────────────── */}
				{activeView === "provider" && (
					<FilterListSubView
						title={t("expenses.mobile.filters.provider")}
						items={providers}
						value={draft.providerId}
						onChange={setDraftProvider}
						onBack={() => setActiveView("main")}
						allLabel={t("expenses.mobile.filters.allProviders")}
					/>
				)}

				{/* ── Sub-vista: categoría ─────────────────────── */}
				{activeView === "category" && (
					<FilterListSubView
						title={t("expenses.mobile.filters.category")}
						items={expense_types}
						value={draft.expenseTypeId}
						onChange={setDraftCategory}
						onBack={() => setActiveView("main")}
						allLabel={t("expenses.mobile.filters.allCategories")}
					/>
				)}

				{/* ── Sub-vista: rango de fechas ──────────────── */}
				{activeView === "dates" && (
					<FilterDatesSubView
						focusField={activeDateField}
						startDate={draft.startDate}
						endDate={draft.endDate}
						onStartDateChange={setDraftStartDate}
						onEndDateChange={setDraftEndDate}
						onBack={() => setActiveView("main")}
					/>
				)}
			</div>
		</div>
	);
};

export default MobileExpenseFiltersSheet;
